/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// icons
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

import { EDIT_MODE, VIEW_MODE } from '../../../utils/const/taskModes';

import useStyles from '../styles';
import QuestionShell from '../QuestionShell';
import useQuestionForm, { QUESTION_FIELDS } from '../QuestionShell/useQuestionForm';
import { removeChoiceFromQuestionLogic } from '../../../utils/questionLogic';

function SimpleSelection({
  identifier,
  pages,
  info,
  handleDeleteQuestion,
  mode,
  handleDuplicateQuestion,
  setPages, // formType,
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const initialId = 1;
  const [correct, setCorrect] = useState(['']);
  const [options, setOptions] = useState([]);

  const question = useQuestionForm({
    identifier,
    pages,
    info,
    setPages,
    writeVia: 'setPages',
    // El blur escribe mutando: con `setPages` se recrearía `pages` y el efecto de
    // carga volvería a resetear opciones y respuesta correcta.
    blurVia: 'pages',
    autoLoadMedia: false,
  });
  const {
    image, setImage, setEmailsArray, setSendEmail,
    scheduleWrite, questionLogic, syncLocalField,
  } = question;

  useEffect(() => {
    if (info) {
      if (image.url !== info.imageUrl || image.name !== info.imageKey) {
        setImage({
          ...image,
          url: info.imageUrl,
          name: info.imageKey,
        });
      }
      if (
        info.QuestionEmails !== undefined
        && info.QuestionEmails.length !== 0
      ) {
        setEmailsArray(info.QuestionEmails[0].emails.split(','));
      }
      setSendEmail(info.sendEmail);
      if (
        info.CorrectQuestionChoices[0]
        && info.CorrectQuestionChoices[0].questionChoiceId
      ) {
        setCorrect(
          Number.parseInt(info.CorrectQuestionChoices[0].questionChoiceId, 10),
        );
      }
      const sortedChoices = [
        ...info.QuestionChoices,
      ].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setOptions(sortedChoices);
    } else if (pages && pages[identifier - 1].questionChoices) {
      const { questionChoices } = pages[identifier - 1];

      // Las borradas quedan afuera de la lista editable pero se conservan en la
      // página: sin separarlas, este efecto las devolvía a la UI y les perdía el
      // flag, así que el API nunca llegaba a darlas de baja.
      const deleted = questionChoices.filter((choice) => choice.deleted);
      const sortedChoices = questionChoices
        .filter((choice) => !choice.deleted)
        .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

      const optionsArray = sortedChoices.map((questionChoice, index) => ({
        title: questionChoice.title,
        type: questionChoice.type,
        identifier: index + 1,
        id: questionChoice.id,
        position: questionChoice.position ?? index,
      }));

      if (pages[identifier - 1].correctQuestionChoices
        && pages[identifier - 1].correctQuestionChoices.length > 0) {
        const correctQuestionChoiceId = pages[
          identifier - 1
        ].correctQuestionChoices[0].questionChoiceId;
        const correctIndex = optionsArray.findIndex(
          (questionChoice) => questionChoice.id === correctQuestionChoiceId,
        );
        setCorrect(Number.parseInt(correctIndex + 1, 10));
        pages[identifier - 1] = {
          ...pages[identifier - 1],
          questionAnswer: [Number.parseInt(correctIndex + 1, 10)],
        };
      }
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionChoices: [...optionsArray, ...deleted],
        questionAnswer: correct,
      };
      setOptions(optionsArray);
      if (
        pages[identifier - 1].questionEmails !== undefined
        && pages[identifier - 1].questionEmails.length !== 0
      ) {
        setEmailsArray(
          pages[identifier - 1].questionEmails[0].emails.split(','),
        );
      }
      setSendEmail(pages[identifier - 1].sendEmail);
    } else {
      setOptions([]);
    }

    if (pages && pages[identifier - 1].imageUrl) {
      setImage({
        ...image,
        url: pages[identifier - 1].imageUrl,
        name: pages[identifier - 1].imageKey,
      });
    }
    // eslint-disable-next-line
  }, [identifier, info, pages]);

  // Las opciones borradas se conservan en `questionChoices` con `deleted: true`
  // para que el API sepa cuáles dar de baja.
  const deletedChoices = () => (pages[identifier - 1].questionChoices || [])
    .filter((choice) => choice.deleted);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newOptions = Array.from(options);
    const [movedItem] = newOptions.splice(result.source.index, 1);
    newOptions.splice(result.destination.index, 0, movedItem);

    const updatedOptions = newOptions.map((opt, index) => ({
      ...opt,
      identifier: index + 1,
      position: index,
    }));

    setOptions(updatedOptions);
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: updatedOptions,
    };
  };

  const handleAddOption = () => {
    if (options.length && !options[options.length - 1].title) return;

    const generatedId = initialId + options.length;
    const newOptions = [
      ...options,
      {
        identifier: `${generatedId}`,
        title: '',
        type: 'text',
        position: options.length,
      },
    ];
    newOptions.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setOptions(newOptions);

    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: [...newOptions, ...deletedChoices()],
    };
  };

  const handleAddOtherOption = () => {
    if (options.length && !options[options.length - 1].title) return;

    const generatedId = initialId + options.length;

    const newOption = {
      identifier: `${generatedId}`,
      title: t('task.taskEdition.simpleSelectionText.other'),
      type: 'other',
      position: options.length,
    };

    const updatedOptions = [...options, newOption];
    updatedOptions.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setOptions(updatedOptions);

    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: [...updatedOptions, ...deletedChoices()],
      questionAnswer: correct,
    };
  };

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    syncLocalField(id, value);

    if (QUESTION_FIELDS.includes(id)) {
      scheduleWrite({
        [id]: value,
        questionLogic,
        questionAnswer: correct,
        imageUrl,
      });
      return;
    }

    const indexOption = options.findIndex(
      (option) => `${option.identifier}` === id,
    );
    if (indexOption === -1) return;

    const newOptions = [...options];
    newOptions[indexOption] = { ...newOptions[indexOption], title: value };
    setOptions(newOptions);

    scheduleWrite({
      questionChoices: [...newOptions, ...deletedChoices()],
      questionAnswer: correct,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correct, options, questionLogic, scheduleWrite, syncLocalField]);

  const handleCorrectAnswer = (event, identifierid) => {
    if (event.target.checked) {
      setCorrect([identifierid]);
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionAnswer: [identifierid],
      };
    }
  };

  const isCorrect = (identifierid) => {
    if (!correct) return false;
    return Number.parseInt(correct, 10) === Number.parseInt(identifierid, 10);
  };

  const handleDeleteOption = (id) => {
    const optionToDelete = options.find(
      (opt) => (
        opt.id?.toString() === id?.toString() || opt.identifier?.toString() === id?.toString()
      ),
    );

    const remainingOptions = options.filter(
      (opt) => (
        opt.id?.toString() !== id?.toString() && opt.identifier?.toString() !== id?.toString()
      ),
    ).map((opt, index) => ({
      ...opt,
      identifier: index + 1,
    }));

    setOptions(remainingOptions);

    const deleted = deletedChoices();
    // Los identificadores de las alternativas se reasignan, así que las
    // conexiones que apuntaban a ellas hay que renumerarlas.
    const questionLogicWithoutChoice = removeChoiceFromQuestionLogic(
      pages[identifier - 1].questionLogic, optionToDelete?.identifier,
    );

    if (optionToDelete?.id) {
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionLogic: questionLogicWithoutChoice,
        questionChoices: [
          ...remainingOptions,
          ...deleted.filter((choice) => choice.id !== optionToDelete.id),
          { ...optionToDelete, deleted: true },
        ],
      };
    } else {
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionLogic: questionLogicWithoutChoice,
        questionChoices: [...remainingOptions, ...deleted],
      };
    }
  };

  return (
    <QuestionShell
      {...question}
      handleOnChange={handleOnChange}
      identifier={identifier}
      info={info}
      mode={mode}
      titleKey="task.taskEdition.simpleSelectionText.question"
      descriptionKey="task.taskEdition.simpleSelectionText.description"
      actionsInBox
      handleDeleteQuestion={handleDeleteQuestion}
      handleDuplicateQuestion={handleDuplicateQuestion}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <div onDragStart={(e) => e.stopPropagation()}>
          <Droppable droppableId="options">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {options.map((option, index) => (
                  <Draggable
                    key={option.identifier || `${option.id}`}
                    draggableId={`${option.identifier || option.id}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: '8px',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'grab',
                        }}
                      >
                        <FormControlLabel
                          className={styles.formControlLabel}
                          control={
                            <Checkbox
                              className={styles.checkbox}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={
                                <RadioButtonCheckedIcon
                                  style={{ color: 'green' }}
                                />
                              }
                              name={`${option.identifier}`}
                              onClick={(event) => handleCorrectAnswer(
                                event,
                                option.identifier,
                              )}
                              checked={isCorrect(
                                option.identifier || `${option.id}`,
                              )}
                              disabled={mode === VIEW_MODE}
                            />
                          }
                          label={
                            option.type === 'other' ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                width="100%"
                              >
                                <Typography className={styles.otherLabel}>
                                  {t('task.taskEdition.simpleSelectionText.other')}:
                                </Typography>
                                <TextField
                                  id={`${option.identifier}`}
                                  variant="standard"
                                  InputProps={{ disableUnderline: true }}
                                  disabled
                                  fullWidth
                                />
                              </Box>
                            ) : (
                              <div className={styles.optionContainer}>
                                <TextField
                                  id={`${option.identifier || option.id}`}
                                  className={styles.optionInput}
                                  value={option.title}
                                  placeholder={`${t(
                                    'task.taskEdition.simpleSelectionText.write',
                                  )} ${t(
                                    'task.taskEdition.simpleSelectionText.option',
                                  )}`}
                                  InputProps={{ disableUnderline: true }}
                                  onChange={
                                    mode === EDIT_MODE ? handleOnChange : null
                                  }
                                  disabled={mode === VIEW_MODE}
                                  multiline
                                  maxRows={5}
                                  style={{ pointerEvents: 'auto', cursor: 'text' }}
                                />
                                <button
                                  className={styles.deleteOptionButton}
                                  onClick={
                                    () => handleDeleteOption(option.identifier || option.id)
                                  }
                                  style={{ display: mode === VIEW_MODE ? 'none' : 'flex' }}
                                >
                                  &times;
                                </button>
                              </div>
                            )
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      <Button
        onClick={handleAddOption}
        className={clsx({
          [styles.hide]: info,
        })}
      >
        {t('task.taskEdition.simpleSelectionText.addOption')}
      </Button>

      <Button
        onClick={handleAddOtherOption}
        className={clsx({
          [styles.hide]: info,
        })}
        disabled={options.some((opt) => opt.type === 'other')}
      >
        {t('task.taskEdition.simpleSelectionText.otherOption')}
      </Button>
    </QuestionShell>
  );
}

export default SimpleSelection;
